import { toRaw } from 'vue';
import timezoneApi from '@/common/api/timezone.api';
import { carrierOf, doseQuantityOf, isHeld, isPrn, segmentOn, todayIn } from '@/common/medicationSchedule';
import { DAY_NAMES, DEFAULT_ADMINISTRATION_WINDOW, intervalLabel, unitName } from '@/common/medicationWindow';
import { formatClockTime } from '@/filters/dateTimeFilters';

const MINUTE_MS = 60000;

const HOUR_MS = 60 * MINUTE_MS;

const DURATION_MS = { s: 1000, sec: 1000, second: 1000, min: 60000, minute: 60000, h: 3600000, hr: 3600000, hour: 3600000, d: 86400000, day: 86400000, wk: 604800000, week: 604800000 };

/**
 * How a nurse satisfied themselves this is the patient.
 *
 * One press records the method, the person and the moment. There is deliberately nothing to type:
 * a date of birth typed at the wrong bedside is as easy to type as at the right one, so what the
 * record is worth is the nurse's assertion of how they knew - which is what this is.
 *
 * Codes are stable identifiers and the labels are copy; 'other' is the only one that carries the
 * nurse's own words alongside it.
 */
export const IDENTITY_METHODS = [
    { code: 'nameAndDobStated', label: 'Name + DOB' },
    { code: 'photoMatch', label: 'Photo match' },
    { code: 'governmentPhotoId', label: 'Photo ID' },
    { code: 'knownToMe', label: 'Known to me' },
    { code: 'wristband', label: 'Wristband' },
    { code: 'other', label: 'Other' },
];

/** The label for a recorded method, for a screen reading one back. */
export const identityMethodLabel = (code) => IDENTITY_METHODS.find((method) => method.code === code)?.label || code || '';

/**
 * Every location the user has been granted, across every agency she can switch to.
 *
 * Read from the switcher's own list, so the eMAR starts from the places the top bar lists -
 * without depending on which one of them is selected there.
 *
 * @param {Array<object>} userAgencies - authStore.userAgencies: [{ id, name, locations: [{ id }] }]
 * @returns {Set<string>}
 */
export const grantedLocationIds = (userAgencies) =>
    new Set(
        (userAgencies || [])
            .flatMap((agency) => agency?.locations || [])
            .map((location) => location?.id)
            .filter(Boolean),
    );

/**
 * The administration site picker's rows: a heading per facility, and each site under it by its own
 * name, with the levels between beneath it. A path repeated down every row makes a nurse read past
 * the same prefix to find the part that differs; the full path stays on the selected value, where
 * two windows of one name in different wings still have to be told apart.
 *
 * One facility needs no heading. Sites arrive sorted by path, so a facility's sites are contiguous.
 *
 * `context` is what follows the name once chosen - the facility and the levels between - so a field
 * too narrow for it cuts the context rather than the name. A facility chosen as itself has none.
 *
 * A heading carries its text in `header` and an EMPTY title: VSelect's keyboard lookup matches the
 * typed prefix against every item's title, headings included, and writes a match straight to the
 * model. See ProgramDialog's levelOfCareOptions.
 *
 * @param {Array<object>} sites - { id, name, label, icon, facilityId, facility, facilityIcon, trail }
 * @returns {Array<object>}
 */
export const siteMenuOf = (sites) => {
    const list = sites || [];
    const grouped = new Set(list.map((site) => site.facilityId)).size > 1;
    const rows = [];
    let current;
    list.forEach((site) => {
        if (grouped && site.facilityId !== current) {
            current = site.facilityId;
            rows.push({ type: 'subheader', id: `facility:${site.facilityId}`, title: '', header: site.facility, icon: site.facilityIcon });
        }
        const context = site.facilityId === site.id ? '' : [site.facility, site.trail].filter(Boolean).join(' › ');
        rows.push({ ...site, title: site.name || site.label, subtitle: site.trail || undefined, context });
    });
    return rows;
};

/**
 * Whether a contact search result belongs to one of the places the user was granted.
 *
 * Excludes only a patient we can positively place somewhere else - absent enrollment data is not
 * evidence of absence.
 *
 * toRaw is load-bearing: BnContactSearch runs this inside the computed that writes
 * temp.episodesOfCare onto these same results, so a tracked read of temp loops the network.
 *
 * @param {object} contact - a result from BnContactSearch
 * @param {Set<string>} locationIds - the granted locations and their descendants
 * @returns {boolean}
 */
export const inAgency = (contact, locationIds) => {
    if (contact?.resourceType !== 'Patient' || !locationIds?.size) return true;
    const raw = toRaw(contact);
    // The enrollment read failed, so where this patient is enrolled is not known. hasAccessToContact
    // refuses on the same flag; this is the eMAR's only narrowing and it must not be the one place
    // a failed read widens instead.
    if (raw.temp?.programsUnknown) return false;
    const placed = (raw.temp?.programs || []).flatMap((program) => program?.locations || []).filter(Boolean);
    if (!placed.length) return true;
    return placed.some((id) => locationIds.has(id));
};

/**
 * The one string every layer of the pass agrees on: a facility wall-clock slot with no
 * timezone offset and seconds always present.
 *
 * An offset is ambiguous in the DST fall-back hour, and Postgres keys its unique index on
 * this exact text — '...T08:00' and '...T08:00:00' would be two different slots — so only
 * one form of it may ever exist.
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @param {string} time - 'HH:mm'
 * @returns {string}
 */
export const slotKey = (date, time) => `${date}T${String(time).slice(0, 5)}:00`;

/**
 * A patient's name as the pass reads it, from raw FHIR rather than a fhirworks model.
 *
 * The census returns plain resources, so `fullName` is not there to lean on. The official name
 * wins where one is marked, because that is the name on the wristband the nurse is checking
 * against - and a patient with no name at all is still owed their medication, so this never
 * returns empty.
 *
 * @param {object} patient - a Patient resource
 * @returns {string}
 */
export const patientNameOf = (patient) => {
    const name = patient?.name?.find((item) => item?.use === 'official') || patient?.name?.[0];
    return name?.text || [name?.given?.join(' '), name?.family].filter(Boolean).join(' ') || 'Unknown patient';
};

/**
 * The facility wall-clock slot as a UTC epoch, for comparison against an instant.
 * serviceTimeZone.js only carries the instant -> local direction; this is its inverse, and
 * is what keeps 08:00 at 08:00 local on both sides of a daylight-saving transition.
 */
const wallClockAt = (wallClock, timeZone) => {
    try {
        return new Date(timezoneApi.getGlobalDateTime(wallClock, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", timeZone)).getTime();
    } catch {
        return NaN;
    }
};

// The slot is facility wall clock by definition - the scheduledDateTime StructureDefinition says
// so, and the unique index that prevents a double dose is keyed on that exact text. A value
// carrying an offset or a Z is malformed, and every screen displays it by slicing the characters
// out, so parsing it as an instant here marks a dose late that the same row shows as on time.
// Read it the way it is shown.
const WALL_CLOCK = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)/;

const slotAt = (expected, timeZone) => {
    const match = WALL_CLOCK.exec(String(expected?.slot || ''));
    return match ? wallClockAt(match[1], timeZone) : NaN;
};

// An offset, a trailing Z, or a Date is already an instant. A bare 'YYYY-MM-DDTHH:mm:ss' is
// facility wall clock and must go through the zone, or it lands in whatever zone the browser
// happens to be in - which silently reads as hours of extra spacing between doses.
const OFFSETLESS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;

export const instantAt = (value, timeZone) => (typeof value === 'string' && OFFSETLESS.test(value) ? wallClockAt(value, timeZone) : new Date(value).getTime());

const windowMs = (expected) => {
    const minutes = Number(expected?.window);
    return (Number.isFinite(minutes) && minutes >= 0 ? minutes : DEFAULT_ADMINISTRATION_WINDOW) * MINUTE_MS;
};

/**
 * A FHIR Duration as milliseconds, or 0 when it states nothing comparable.
 *
 * @param {object} duration
 * @returns {number}
 */
export const durationMs = (duration) => {
    const value = Number(duration?.value);
    if (!Number.isFinite(value) || value <= 0) return 0;
    const unit = String(duration?.code || duration?.unit || '').toLowerCase();
    const factor = DURATION_MS[unit] ?? DURATION_MS[unit.replace(/s$/, '')];
    return factor ? value * factor : 0;
};

/**
 * When a dose was given.
 *
 * `effective` is a FHIR choice type: a read returns `{dateTime}` or `{Period}`, and a record this
 * app wrote may still be a bare string. Every screen that shows a documented dose needs all three
 * readings, so this lives here rather than being rewritten per component - four copies had already
 * dropped the Period branch before it was.
 */
export const effectiveOf = (administration) => {
    const effective = administration?.effective;
    if (typeof effective === 'string') return effective;
    return effective?.dateTime ?? effective?.Period?.start ?? null;
};

/**
 * An amount and its unit as one string - '2 tablet' - or '' when nothing was recorded.
 *
 * Only the unit is dropped when absent. Filtering the amount too loses a recorded zero, and a
 * dose given as none reads as the bare unit: 'tablet' where the record says '0 tablet'.
 */
export const quantityOf = (dose) =>
    dose?.value === undefined || dose?.value === null ? '' : [dose.value, dose.unit || dose.code].filter((part) => part !== undefined && part !== null && part !== '').join(' ');

/** The patient's medical record number, as every eMAR surface names them alongside their name. */
export const mrnOf = (patient) => {
    const identifiers = (patient?.identifier || []).filter((item) => item?.value);
    // Selected by type, not by position: an SSN or a member id ordered first would otherwise
    // read as the record number a nurse confirms identity against. Falls back to the first
    // identifier only when nothing is typed MR, which is what the pass showed before.
    const medicalRecord = identifiers.find((item) => (item?.type?.coding || []).some((coding) => coding?.code === 'MR'));
    return (medicalRecord || identifiers[0])?.value || '';
};

/**
 * Who gave the dose.
 *
 * The performer function code where one is stated, the first actor otherwise: a record may name a
 * witness alongside the nurse, and the nurse is the one the screen is naming.
 */
export const performerOf = (administration) => {
    const performers = administration?.performer || [];
    const gave = performers.find((entry) => entry?.function?.coding?.[0]?.code === 'performer') || performers[0];
    return gave?.actor?.display || '';
};

/** The facility's clock, never the browser's - the nurse reads the time the unit runs on. */
export const atFacilityIn = (instant, pattern, timeZone) => {
    if (!instant) return '';
    try {
        return timezoneApi.getLocalDateTime(instant, pattern, timeZone || undefined);
    } catch {
        return '';
    }
};

/**
 * When a dose was given, on the facility's clock and always dated: an as-needed dose can be from
 * yesterday, and a bare '11:30 PM' would read as tonight. The PRN screen and the pass list both use
 * it, so one dose reads the same on each.
 */
export const givenAtLabelOf = (instant, timeZone) => {
    const day = atFacilityIn(instant, 'M/d/yyyy', timeZone);
    return day ? `${day} ${formatClockTime(atFacilityIn(instant, 'HH:mm', timeZone))}` : '';
};

/**
 * A dose given against an as-needed order: given, answering no slot, and carrying no category.
 *
 * The same rule History and the reports already use to call a dose as-needed. A slot makes it
 * scheduled and `category: unscheduled` makes it stand-alone, and neither owes a follow-up.
 */
export const isPrnDose = (administration) => Boolean(administration) && administration.status === 'completed' && !administration.scheduledDateTime && !administration.category?.coding?.length;

const UNKNOWN_AUTHOR = 'Unknown user';

/**
 * Every effectiveness follow-up written on a dose, oldest first.
 *
 * An entry with no text says nothing and is dropped rather than shown as a blank stamp. An author
 * with no name (BNSupport, a deleted person) still reads as somebody rather than as nobody.
 *
 * @returns {Array<{text: string, time: string, authorName: string}>}
 */
export const effectivenessEntriesOf = (administration) =>
    (administration?.effectiveness || [])
        .filter((entry) => String(entry?.text || '').trim())
        .map((entry) => ({ text: entry.text, time: entry.time || '', authorName: entry.author?.display || UNKNOWN_AUTHOR }))
        .sort((a, b) => (Date.parse(a.time) || 0) - (Date.parse(b.time) || 0));

/** An as-needed dose nobody has yet said worked or did not. The first entry settles it. */
export const owesEffectiveness = (administration) => isPrnDose(administration) && effectivenessEntriesOf(administration).length === 0;

/** What an as-needed dose reads as until somebody says whether it worked. */
export const EFFECTIVENESS_NOT_RECORDED = 'Effectiveness not recorded';

/** Date and time as the follow-up surfaces state them, on the facility's clock. */
export const EFFECTIVENESS_TIME_PATTERN = 'M/d/yyyy h:mm a';

/** Who recorded an entry and when - the one stamp the PRN screen, History and the med list all show. */
export const effectivenessStampOf = (entry, timeZone) => [entry?.authorName, atFacilityIn(entry?.time, EFFECTIVENESS_TIME_PATTERN, timeZone)].filter(Boolean).join(' · ');

const quantityUnit = (quantity) => String(quantity?.code || quantity?.unit || '').toLowerCase();

/**
 * Pair each expectation with the record that answers it, or null.
 *
 * The match is on the scheduled slot and never on when the dose was actually given, which is
 * what lets a 23:30 dose given at 00:15 still answer its own slot. A stand-alone record has no
 * slot — it may still name the order it fulfils — so it answers nothing and is never reported as
 * an orphan: it is the emergency dose the double-dose constraint deliberately exempts, and the
 * partial unique index skips it for the same reason, needing both columns non-null.
 *
 * @param {Array<object>} expected
 * @param {Array<object>} administrations
 * @returns {Array<{expected: object, administration: object|null}>} one entry per expectation, in the order given
 */
export const matchAdministrations = (expected, administrations) => {
    const records = new Map();
    for (const administration of administrations || []) {
        if (String(administration?.status || '').toLowerCase() === 'entered-in-error') continue;
        const requestId = administration?.request?.id;
        const slot = administration?.scheduledDateTime;
        if (!requestId || !slot) continue;
        const key = `${requestId}|${slot}`;
        if (!records.has(key)) records.set(key, administration);
    }
    return (expected || []).map((item) => ({ expected: item, administration: records.get(`${item?.medicationId}|${item?.slot}`) ?? null }));
};

/**
 * Whether a dose was given early, on time or late against its slot's administration window.
 * Derived on every read; nothing is stored.
 *
 * @param {object} administration
 * @param {object} expected
 * @param {string} [timeZone]
 * @returns {'early'|'onTime'|'late'}
 */
export const timingOf = (administration, expected, timeZone) => {
    const delta = timingDeltaOf(administration, expected, timeZone);
    if (delta === null) return 'onTime';

    const tolerance = windowMs(expected) / MINUTE_MS;
    if (delta < -tolerance) return 'early';
    if (delta > tolerance) return 'late';
    return 'onTime';
};

/**
 * How far off its slot a dose was given, in whole minutes - negative early, positive late.
 *
 * `timingOf` says which side of the window a dose fell; a report has to say by how much, and
 * order the list by it. Both read this so the band and the number can never disagree.
 *
 * @param {object} administration
 * @param {object} expected - anything carrying a `slot`
 * @param {string} [timeZone]
 * @returns {number|null} null when either instant is unresolvable
 */
export const timingDeltaOf = (administration, expected, timeZone) => {
    // Guarded before the Date: new Date(null) is the epoch, which is finite, so a record with no
    // effective time would otherwise measure fifty-six years early rather than reading as unknown.
    const effective = effectiveOf(administration);
    if (!effective) return null;

    // instantAt, not new Date: an offsetless effective is facility wall clock, and reading it in
    // the browser's zone here while prnBreachesOf reads it in the facility's makes the same record
    // late on one screen and on time on another.
    const actual = instantAt(effective, timeZone);
    const slot = slotAt(expected, timeZone);
    if (!Number.isFinite(actual) || !Number.isFinite(slot)) return null;
    return Math.round((actual - slot) / MINUTE_MS);
};

/**
 * A signed minute distance in words - '3h 12m', '45m'. The sign is the caller's to state: a
 * report says 'late' or 'early' beside it, and the pass says how overdue a dose is.
 *
 * @param {number} minutes
 * @returns {string}
 */
export const minutesLabel = (minutes) => {
    const whole = Math.abs(Math.round(Number(minutes) || 0));
    const hours = Math.floor(whole / 60);
    return hours ? `${hours}h ${String(whole % 60).padStart(2, '0')}m` : `${whole}m`;
};

/**
 * Was less given than ordered? Units that disagree are not comparable, so they are not partial.
 *
 * @param {object} administration
 * @param {object} expected
 * @returns {boolean}
 */
export const isPartial = (administration, expected) => {
    const given = Number(administration?.dosage?.dose?.value);
    const ordered = Number(expected?.dose?.value);
    if (!Number.isFinite(given) || !Number.isFinite(ordered)) return false;
    if (quantityUnit(administration?.dosage?.dose) !== quantityUnit(expected?.dose)) return false;
    return given < ordered;
};

/**
 * Where a dose is recorded as having been given: where the nurse is working.
 *
 * The place she has the screen narrowed to, else the location she works at. Two facts about one
 * dose are being kept apart here — the order says where it should be given, the record says where
 * it was, and that difference is the whole reason for having both fields. So this never reads the
 * order and never reads the dose.
 *
 * Never nothing. A cleared filter is a nurse widening her screen, not a statement that she is
 * nowhere, and a record naming nowhere is invisible to every location-scoped read afterwards.
 * Where both are absent the caller refuses the write rather than guessing.
 *
 * @param {string|null} selectedLocationId - the place the pass is narrowed to
 * @param {string|null} facilityLocationId - the location the user works at
 * @returns {string|null}
 */
export const administeringLocationFor = (selectedLocationId, facilityLocationId) => selectedLocationId || facilityLocationId || null;

/**
 * Would recording this dose at `effectiveAt` leave it too close to the medication's next
 * scheduled dose? A safety warning, not a block.
 *
 * @param {Date|string} effectiveAt - a Date or an offset-bearing instant, OR a bare
 *   'YYYY-MM-DDTHH:mm:ss' facility wall clock, which is resolved through `timeZone`
 * @param {object} expected
 * @param {object|null} nextExpected - the caller's next expectation for the SAME medication
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export const violatesMinimumInterval = (effectiveAt, expected, nextExpected, timeZone) => {
    if (!nextExpected) return false;
    const minimum = durationMs(expected?.minimumInterval);
    if (!minimum) return false;

    const at = instantAt(effectiveAt, timeZone);
    const next = slotAt(nextExpected, timeZone);
    if (!Number.isFinite(at) || !Number.isFinite(next)) return false;
    return Math.abs(next - at) < minimum;
};

/**
 * A FHIR Duration in words — '4 hours', '90 minutes'.
 *
 * Built on medicationWindow's unitName rather than a private map: that module's own header records
 * that it exists because three such maps had drifted apart, and a fourth would render half of this
 * screen (the stated minimum, and the breach message about it) in different words.
 *
 * @param {object} duration
 * @returns {string}
 */
export const durationLabel = (duration) => {
    const value = Number(duration?.value);
    if (!Number.isFinite(value)) return '';
    return `${value} ${duration?.unit || unitName(duration?.code, value)}`.trim();
};

const ORDINAL_SUFFIXES = ['th', 'st', 'nd', 'rd'];

const ordinal = (count) => {
    const tens = count % 100;
    // 11th, 12th and 13th are the exception to what their last digit would otherwise say.
    const suffix = tens >= 11 && tens <= 13 ? 'th' : (ORDINAL_SUFFIXES[count % 10] ?? 'th');
    return `${count}${suffix}`;
};

/** Time since a dose in words - '16h', '1h 5m', '45m'. */
export const elapsedLabel = (ms) => {
    const minutes = Math.floor(ms / MINUTE_MS);
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return `${minutes}m`;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/**
 * Memoized on the facility day, not the instant.
 *
 * Reading a series measures every dose against all the doses before it, so each dose's instant is
 * converted once per comparison rather than once - and each conversion is a date-fns-tz zone
 * resolution, one of the more expensive operations in the app.
 */
const DAY_KEY_CACHE = new Map();
const DAY_KEY_CACHE_MAX = 5000;

const dayKeyAt = (ms, timeZone) => {
    if (!Number.isFinite(ms)) return null;
    // Keyed on the instant, never on a UTC day: a facility day straddles two of those, and the
    // repetition being avoided is the same dose converted once per comparison, not distinct doses
    // sharing a day.
    const key = `${ms}|${timeZone || ''}`;
    if (DAY_KEY_CACHE.has(key)) return DAY_KEY_CACHE.get(key);
    let day = null;
    try {
        day = timezoneApi.getLocalDateTime(new Date(ms).toISOString(), 'yyyy-MM-dd', timeZone);
    } catch {
        day = null;
    }
    if (DAY_KEY_CACHE.size >= DAY_KEY_CACHE_MAX) DAY_KEY_CACHE.clear();
    DAY_KEY_CACHE.set(key, day);
    return day;
};

/**
 * The plain FHIR behind a fhirworks element.
 *
 * A read comes back as models, and the dose is spread into the amount the nurse gives - which on a
 * model copies its private `__field` properties onto the payload and gets the whole record
 * rejected as unrecognized properties. Same guard as useMedPassDoseRecording's.
 */
const plain = (element) => (typeof element?.toJSON === 'function' ? element.toJSON() : element);

/**
 * What the order says about giving this medication today: the amount, the route, the instructions,
 * the medication's reason for taking it, and - only when the segment in force is PRN - the
 * indication and limits it may be given within.
 *
 * The indication and the reason are two facts and are never merged. The indication is the
 * prescriber's, on the PRN window; the reason is the medication list's, which staff or the patient
 * may have written. Presenting one as the other would lend a patient's words a prescriber's
 * authority.
 *
 * A held medication answers null. A hold is a prescriber pausing the order, and neither an
 * as-needed dose nor an emergency one may quietly step past it.
 *
 * With no schedule in force the order's own dose and route answer, and it is never PRN: a dose
 * given at admission or in an emergency comes before anyone has scheduled the order.
 *
 * @param {object} medication
 * @param {string} date
 * @param {string} [timeZone]
 * @returns {{prn: boolean, maxPerDay: number|null, minimumInterval: object|null, dose: object|null, route: object|null, instruction: string, indication: object|null, reason: object|null}|null}
 */
export const administrationDetailsOf = (medication, date, timeZone) => {
    const segment = segmentOn(medication, date, timeZone);
    if (isHeld(segment)) return null;

    const carrier = carrierOf(medication);
    const prn = isPrn(segment);
    const maxPerDay = Number(segment?.maxPerDay);

    return {
        prn,
        maxPerDay: prn && Number.isFinite(maxPerDay) && maxPerDay > 0 ? maxPerDay : null,
        minimumInterval: prn ? (segment.minimumInterval ?? null) : null,
        dose: plain(doseQuantityOf(segment) ?? doseQuantityOf(carrier)) ?? null,
        route: plain(segment?.route ?? carrier?.route) ?? null,
        instruction: segment?.patientInstruction || carrier?.patientInstruction || '',
        indication: prn ? (segment.asNeeded?.CodeableConcept ?? null) : null,
        reason: medication?.reasonCode?.[0] ?? null,
    };
};

/**
 * When the schedule in force gives this order's doses - times, days, and any interval past daily -
 * or '' when no schedule is in force.
 */
export const scheduleLabelOf = (medication, date, timeZone) => {
    const repeat = segmentOn(medication, date, timeZone)?.timing?.repeat;
    if (!repeat) return '';
    const times = [...(repeat.timeOfDay || [])]
        .sort()
        .map((time) => formatClockTime(String(time).slice(0, 5)))
        .filter(Boolean)
        .join(', ');
    const days = (repeat.dayOfWeek || []).map((code) => DAY_NAMES[code] || code).join(', ');
    const everyDay = (Number(repeat.period) || 1) === 1 && (repeat.periodUnit || 'd') === 'd';
    return [times || 'no times stated', days || null, repeat.periodUnit && !everyDay ? intervalLabel(repeat) : null].filter(Boolean).join(' · ');
};

/**
 * The same, narrowed to an order that stands as needed today. Null on anything else, which is what
 * decides whether a medication is offered under As needed.
 */
export const prnLimitsOf = (medication, date, timeZone) => {
    const details = administrationDetailsOf(medication, date, timeZone);
    return details?.prn ? details : null;
};

/**
 * The same numbers, said about a dose that is about to be given or one already given.
 *
 * A report reading the cart's wording would tell a director what a nurse "would" do about a dose
 * given last Tuesday, and a second set of words for the same arithmetic would drift from it.
 */
const BREACH_VOICE = {
    prospective: {
        overDailyMaximum: (position, maxPerDay) => `This would be the ${ordinal(position)} of ${maxPerDay} allowed today`,
        tooSoon: (msSinceLast, minimumInterval) => `Last dose ${elapsedLabel(msSinceLast)} ago, minimum ${durationLabel(minimumInterval)}`,
    },
    recorded: {
        overDailyMaximum: (position, maxPerDay) => `The ${ordinal(position)} of ${maxPerDay} allowed that day`,
        tooSoon: (msSinceLast, minimumInterval) => `Given ${elapsedLabel(msSinceLast)} after the previous dose, minimum ${durationLabel(minimumInterval)}`,
    },
};

/**
 * What has already happened against those limits, and whether one more dose would pass them.
 *
 * A warning and never a block: the nurse decides, and the messages state the order's own numbers
 * so she is deciding against what was written rather than against a rule the screen invented.
 *
 * @param {object} options
 * @param {object|null} options.limits - a prnLimitsOf result
 * @param {Array<object>} options.administrations - this medication's records; only completed ones count
 * @param {string|Date} [options.at] - when the dose would be given; defaults to now
 * @param {string} [options.timeZone]
 * @param {'prospective'|'recorded'} [options.voice] - whether the messages describe a dose about to
 *   be given or one already given. The numbers are identical either way
 * @returns {{givenToday: number, lastGivenAt: string|null, msSinceLast: number|null, overDailyMaximum: boolean, tooSoon: boolean, messages: string[]}}
 */
export const prnBreachesOf = ({ limits, administrations, at, timeZone, voice } = {}) => {
    const atMs = instantAt(at || new Date().toISOString(), timeZone);
    // The count is against the facility's day, not the browser's: a nurse in another zone must
    // not see a fresh allowance, or an exhausted one, at a boundary the clinic has not reached.
    const day = dayKeyAt(atMs, timeZone);

    let givenToday = 0;
    let lastMs = NaN;
    let lastGivenAt = null;
    for (const administration of administrations || []) {
        if (String(administration?.status || '').toLowerCase() !== 'completed') continue;
        const effective = effectiveOf(administration);
        const ms = instantAt(effective, timeZone);
        if (!Number.isFinite(ms)) continue;
        if (day && dayKeyAt(ms, timeZone) === day) givenToday += 1;
        if (!Number.isFinite(lastMs) || ms > lastMs) {
            lastMs = ms;
            lastGivenAt = effective;
        }
    }

    const msSinceLast = Number.isFinite(lastMs) ? atMs - lastMs : null;
    const maxPerDay = Number(limits?.maxPerDay);
    const minimum = durationMs(limits?.minimumInterval);

    const overDailyMaximum = maxPerDay > 0 && givenToday + 1 > maxPerDay;
    const tooSoon = minimum > 0 && msSinceLast !== null && msSinceLast < minimum;

    const words = BREACH_VOICE[voice] || BREACH_VOICE.prospective;
    const messages = [];
    if (overDailyMaximum) messages.push(words.overDailyMaximum(givenToday + 1, maxPerDay));
    if (tooSoon) messages.push(words.tooSoon(msSinceLast, limits.minimumInterval));

    return { givenToday, lastGivenAt, msSinceLast, overDailyMaximum, tooSoon, messages };
};

const categoryCodeOf = (administration) => administration?.category?.coding?.[0]?.code || null;

/**
 * Which already-given doses of one order breached its limits, read back over the whole series.
 *
 * The prospective check answers "may I give one more"; a report and a correction both have to
 * answer "which of these should not have happened". Each dose is measured against the doses
 * before it through `prnBreachesOf`, so the rule has exactly one implementation and a breach can
 * only ever land on the later of the two doses that produced it.
 *
 * @param {object} options
 * @param {object|null} [options.limits] - a prnLimitsOf result, where one set covers the series
 * @param {(dayKey: string) => object|null} [options.limitsFor] - the limits as they stood on a
 *   given facility day. A series spanning a taper step or a hold is measured against what was
 *   ordered on the day of each dose, not against whatever the last day happened to say.
 * @param {Array<object>} options.administrations - every record for the order, in any order
 * @param {string} [options.timeZone]
 * @returns {Array<{administration: object, overDailyMaximum: boolean, tooSoon: boolean, messages: string[]}>}
 *   in effective order, one entry per breaching dose
 */
export const prnBreachesInSeries = ({ limits, limitsFor, administrations, timeZone } = {}) => {
    const given = (administrations || [])
        .filter((administration) => String(administration?.status || '').toLowerCase() === 'completed')
        .map((administration) => ({ administration, ms: instantAt(effectiveOf(administration), timeZone) }))
        .filter((item) => Number.isFinite(item.ms))
        .sort((a, b) => a.ms - b.ms);

    const dayOfDose = (item) => dayKeyAt(item.ms, timeZone);

    const breaches = [];
    given.forEach((item, index) => {
        // A stand-alone dose is never flagged - nothing authorised it in advance, so there was no
        // limit for it to pass. It still counts toward every other dose's daily total, because the
        // patient did receive the medication.
        if (categoryCodeOf(item.administration) === 'unscheduled') return;

        const onTheDay = limitsFor ? limitsFor(dayOfDose(item)) : limits;
        // An order held or discontinued on the day of one dose still has limits on the others.
        if (!onTheDay) return;

        const result = prnBreachesOf({
            limits: onTheDay,
            administrations: given.slice(0, index).map((earlier) => earlier.administration),
            at: effectiveOf(item.administration),
            timeZone,
            voice: 'recorded',
        });
        if (result.overDailyMaximum || result.tooSoon) {
            breaches.push({ administration: item.administration, overDailyMaximum: result.overDailyMaximum, tooSoon: result.tooSoon, messages: result.messages });
        }
    });
    return breaches;
};

/** How far back an as-needed order's doses are read: last night's dose is named this morning. */
export const PRN_LOOKBACK_MS = 24 * HOUR_MS;

/**
 * How far back these orders' records have to be read: the facility's day for the count, a day for
 * the last dose, and the longest minimum interval among them for the spacing.
 *
 * @param {Array<object>} medications
 * @param {{now?: number, timeZone?: string}} [options]
 * @returns {string} an instant
 */
export const prnHistorySince = (medications, { now = Date.now(), timeZone } = {}) => {
    const day = dayKeyAt(now, timeZone) || todayIn(timeZone);
    const bounds = [now - PRN_LOOKBACK_MS];
    const midnight = wallClockAt(`${day}T00:00:00`, timeZone);
    if (Number.isFinite(midnight)) bounds.push(midnight);
    for (const medication of medications || []) bounds.push(now - durationMs(prnLimitsOf(medication, day, timeZone)?.minimumInterval));
    return new Date(Math.min(...bounds)).toISOString();
};

// The strictest limits any order of the drug states today: the longest interval, the lowest maximum.
const strictestLimitsOf = (orders, day, timeZone) => {
    let merged = null;
    for (const order of orders) {
        const limits = prnLimitsOf(order, day, timeZone);
        if (!limits) continue;
        merged = merged || { maxPerDay: null, minimumInterval: null };
        if (durationMs(limits.minimumInterval) > durationMs(merged.minimumInterval)) merged.minimumInterval = limits.minimumInterval;
        if (limits.maxPerDay > 0 && (!merged.maxPerDay || limits.maxPerDay < merged.maxPerDay)) merged.maxPerDay = limits.maxPerDay;
    }
    return merged;
};

/**
 * Whether one more dose of this drug can be given now, across every live order for it.
 *
 * The doses of every order of the drug are measured against the strictest limits any of them
 * states, through `prnBreachesOf`, so a dose on one order is never invisible to another. Because
 * the order's own doses and limits are a subset of those, this never reads Available where the
 * order's own check would warn. What only the other orders add is `crossOrderOnly`: a warning,
 * never a new required reason.
 *
 * @param {object} options
 * @param {object} options.medication - the order being given
 * @param {Array<object>} [options.sameDrugOrders] - every other live order of the same drug
 * @param {Record<string, Array<object>|undefined>} options.histories - by order id; undefined is unread
 * @param {string|Date} [options.at] - defaults to now
 * @param {string} [options.timeZone]
 * @param {boolean} [options.unscheduled] - given outside any as-needed order, so it has no limits of its own
 * @returns {{state: 'unknown'|'dailyMax'|'tooSoon'|'notGiven'|'available', lastGivenAt: string|null, lastGivenOrderId: string|null, fromOtherOrder: boolean, msSinceLast: number|null, nextAllowedAt: string|null, givenToday: number, maxPerDay: number|null, minimumInterval: object|null, noMinimumInterval: boolean, crossOrderOnly: boolean, messages: string[]}}
 *   `messages` are prnBreachesOf's own words for the merged limits, so a cross-order warning states
 *   the numbers exactly as the order's own warning does.
 */
export const prnStatusOf = ({ medication, sameDrugOrders = [], histories = {}, at, timeZone, unscheduled = false } = {}) => {
    const group = [medication, ...(sameDrugOrders || []).filter((order) => order?.id && order.id !== medication?.id)];
    const atIso = at instanceof Date ? at.toISOString() : at || new Date().toISOString();
    const day = dayKeyAt(instantAt(atIso, timeZone), timeZone) || todayIn(timeZone);
    const ownLimits = unscheduled ? null : prnLimitsOf(medication, day, timeZone);
    const limits = strictestLimitsOf(group, day, timeZone);

    const status = {
        state: 'unknown',
        lastGivenAt: null,
        lastGivenOrderId: null,
        fromOtherOrder: false,
        msSinceLast: null,
        nextAllowedAt: null,
        givenToday: 0,
        maxPerDay: limits?.maxPerDay ?? null,
        minimumInterval: limits?.minimumInterval ?? null,
        noMinimumInterval: Boolean(ownLimits) && !durationMs(ownLimits.minimumInterval),
        crossOrderOnly: false,
        messages: [],
    };
    // A read that failed, or has not landed, is not an empty history.
    if (group.some((order) => histories?.[order?.id] === undefined)) return status;

    const seen = new Set();
    const records = [];
    for (const order of group) {
        for (const record of histories[order.id]) {
            if (String(record?.status || '').toLowerCase() === 'entered-in-error') continue;
            if (record?.id) {
                if (seen.has(record.id)) continue;
                seen.add(record.id);
            }
            records.push({ record, orderId: record?.request?.id || order.id });
        }
    }

    const breaches = prnBreachesOf({ limits, administrations: records.map((item) => item.record), at: atIso, timeZone });

    // The same dose prnBreachesOf named: the first completed record at the latest instant.
    let latest = null;
    let latestMs = NaN;
    for (const item of records) {
        if (String(item.record?.status || '').toLowerCase() !== 'completed') continue;
        const ms = instantAt(effectiveOf(item.record), timeZone);
        if (Number.isFinite(ms) && (!Number.isFinite(latestMs) || ms > latestMs)) {
            latest = item;
            latestMs = ms;
        }
    }

    if (breaches.overDailyMaximum) status.state = 'dailyMax';
    else if (breaches.tooSoon) status.state = 'tooSoon';
    else status.state = breaches.lastGivenAt ? 'available' : 'notGiven';

    status.lastGivenAt = breaches.lastGivenAt;
    status.lastGivenOrderId = latest?.orderId ?? null;
    status.fromOtherOrder = Boolean(latest) && latest.orderId !== medication?.id;
    status.msSinceLast = breaches.msSinceLast;
    status.givenToday = breaches.givenToday;
    status.messages = breaches.messages;
    if (status.state === 'tooSoon') status.nextAllowedAt = new Date(latestMs + durationMs(limits.minimumInterval)).toISOString();

    if (status.state === 'tooSoon' || status.state === 'dailyMax') {
        const own = prnBreachesOf({ limits: ownLimits, administrations: histories[medication?.id], at: atIso, timeZone });
        status.crossOrderOnly = !own.overDailyMaximum && !own.tooSoon;
    }
    return status;
};

export default {
    slotKey,
    patientNameOf,
    inAgency,
    matchAdministrations,
    timingOf,
    timingDeltaOf,
    minutesLabel,
    effectiveOf,
    quantityOf,
    atFacilityIn,
    mrnOf,
    performerOf,
    isPrnDose,
    effectivenessEntriesOf,
    owesEffectiveness,
    isPartial,
    violatesMinimumInterval,
    administrationDetailsOf,
    prnLimitsOf,
    durationLabel,
    givenAtLabelOf,
    prnBreachesOf,
    prnBreachesInSeries,
    PRN_LOOKBACK_MS,
    prnHistorySince,
    prnStatusOf,
};
